import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  catchError,
} from 'rxjs/operators';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { InvitationApiService } from '../services/invitation-api.service';
import {
  CreateInvitationRequest,
  InvitationResponse,
  RoleResponse,
} from '../models/invitation.model';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { Contact } from '../../contactos/models/contacto.model';
import { InvitationTemplateApiService } from '../../invitation-templates/services/invitation-template-api.service';
import {
  InvitationTemplateResponse,
  InvitationTemplatePreviewRequest,
} from '../../invitation-templates/models/invitation-template.model';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-invitation-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent],
  templateUrl: './invitation-form.component.html',
})
export class InvitationFormComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<InvitationResponse>();
  @Output() cancelled = new EventEmitter<void>();

  // ── Form state ──────────────────────────────────────────────────────────────
  saving = false;
  error = '';

  mail = '';
  roleId: number | null = null;
  contactId: number | null = null;
  expiresAt = '';
  expiresTime = '';
  minExpiresDate = '';
  templateId: number | null = null;

  // ── Contact typeahead ───────────────────────────────────────────────────────
  contactSearchTerm = '';
  contactSearchResults: Contact[] = [];
  showContactDropdown = false;
  searchingContacts = false;
  loadingMoreContacts = false;
  contactSearchPage = 0;
  contactSearchTotalPages = 0;
  mailAutoFilled = false;

  // ── Roles catalog ───────────────────────────────────────────────────────────
  roles: RoleResponse[] = [];
  rolesLoading = false;

  // ── Templates catalog ─────────────────────────────────────────────────────
  templates: InvitationTemplateResponse[] = [];
  templatesLoading = false;
  selectedTemplate: InvitationTemplateResponse | null = null;

  // ── Vista previa (renderer autoritativo del backend, idéntico al correo real) ─
  previewLoading = false;
  previewError = '';
  previewSafeHtml: SafeHtml | null = null;
  previewSubject = '';

  private readonly contactSearch$ = new Subject<string>();
  private readonly preview$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  readonly hourOptions: string[] = Array.from(
    { length: 24 },
    (_, h) => `${h.toString().padStart(2, '0')}:00`,
  );
  constructor(
    private invitationApi: InvitationApiService,
    private contactoApi: ContactoApiService,
    private authService: AuthService,
    private templateApi: InvitationTemplateApiService,
    private datetimeService: DatetimeService,
    private sanitizer: DomSanitizer,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadRoles();
    this.loadTemplates();
    this.setupContactSearch();
    this.setupPreview();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Roles ───────────────────────────────────────────────────────────────────

  private loadRoles(): void {
    this.rolesLoading = true;
    this.invitationApi.listRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        this.rolesLoading = false;
        this.preview$.next();
      },
      error: () => {
        this.rolesLoading = false;
      },
    });
  }

  /** Dispara un refresco de la vista previa (mail, rol, expiración, plantilla). */
  onPreviewInputChange(): void {
    this.preview$.next();
  }

  // ── Contact typeahead ───────────────────────────────────────────────────────

  private setupContactSearch(): void {
    this.contactSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.contactSearchPage = 0;
          this.contactSearchResults = [];
          this.contactSearchTotalPages = 0;

          if (term.length === 0) {
            this.showContactDropdown = false;
            return of(null);
          }

          this.searchingContacts = true;
          const businessId = this.authService.getCurrentUser()?.businessId || 1;
          return this.contactoApi
            .searchContacts({ businessId, searchTerm: term, page: 0, size: 10 })
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingContacts = false;
        if (response) {
          this.contactSearchResults = response.content;
          this.contactSearchTotalPages = response.totalPages;
          this.showContactDropdown = this.contactSearchResults.length > 0;
        }
      });
  }

  onContactSearchInput(term: string): void {
    this.contactSearchTerm = term;
    this.contactId = null;
    this.contactSearch$.next(term);
  }

  onContactSearchEnter(event: Event): void {
    event.preventDefault();
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.contactSearchPage = 0;
    this.contactSearchResults = [];
    this.contactSearchTotalPages = 0;
    this.searchingContacts = true;
    this.showContactDropdown = false;

    this.contactoApi
      .searchContacts({
        businessId,
        searchTerm: this.contactSearchTerm.trim(),
        page: 0,
        size: 10,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingContacts = false;
        if (response) {
          this.contactSearchResults = response.content;
          this.contactSearchTotalPages = response.totalPages;
          this.showContactDropdown = this.contactSearchResults.length > 0;
        }
      });
  }

  onContactDropdownScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom || this.loadingMoreContacts) return;
    if (this.contactSearchPage + 1 >= this.contactSearchTotalPages) return;

    this.loadingMoreContacts = true;
    this.contactSearchPage++;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    this.contactoApi
      .searchContacts({
        businessId,
        searchTerm: this.contactSearchTerm.trim(),
        page: this.contactSearchPage,
        size: 10,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.loadingMoreContacts = false;
        if (response) {
          this.contactSearchResults = [
            ...this.contactSearchResults,
            ...response.content,
          ];
          this.contactSearchTotalPages = response.totalPages;
        }
      });
  }

  selectContact(contact: Contact): void {
    this.contactId = contact.id!;
    this.contactSearchTerm = `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`;
    this.showContactDropdown = false;
    this.contactSearchResults = [];

    if (contact.mail && !this.mail) {
      this.mail = contact.mail;
      this.mailAutoFilled = true;
      setTimeout(() => {
        this.mailAutoFilled = false;
      }, 2000);
    }
    this.preview$.next();
  }

  closeContactDropdown(): void {
    setTimeout(() => {
      this.showContactDropdown = false;
    }, 200);
  }

  // ── Templates ─────────────────────────────────────────────────────────────

  private loadTemplates(): void {
    this.templatesLoading = true;
    this.templateApi.list(0, 100).subscribe({
      next: (res) => {
        this.templates = res.content;
        this.templatesLoading = false;
        // Auto-seleccionar la plantilla default
        const defaultTpl = this.templates.find((t) => t.isDefault);
        if (defaultTpl && !this.templateId) {
          this.templateId = defaultTpl.id;
          this.selectedTemplate = defaultTpl;
        }
        this.preview$.next();
      },
      error: () => {
        this.templatesLoading = false;
      },
    });
  }

  onTemplateChange(id: number | null): void {
    this.templateId = id;
    this.selectedTemplate = id
      ? (this.templates.find((t) => t.id === id) ?? null)
      : null;
    this.preview$.next();
  }

  // ── Vista previa vía endpoint (mismo renderer que el envío real) ─────────────

  private setupPreview(): void {
    this.preview$
      .pipe(
        debounceTime(400),
        switchMap(() => {
          if (!this.selectedTemplate) {
            this.previewSafeHtml = null;
            this.previewSubject = '';
            this.previewError = '';
            return of(null);
          }
          this.previewLoading = true;
          this.previewError = '';
          return this.templateApi
            .preview(this.buildPreviewRequest(this.selectedTemplate))
            .pipe(
              catchError((err) => {
                this.previewError =
                  err.error?.message || 'No se pudo generar la vista previa.';
                return of(null);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => {
        this.previewLoading = false;
        if (res) {
          this.previewSubject = res.subject;
          this.previewSafeHtml = this.sanitizer.bypassSecurityTrustHtml(
            res.html,
          );
        }
      });
  }

  private buildPreviewRequest(
    tpl: InvitationTemplateResponse,
  ): InvitationTemplatePreviewRequest {
    const user = this.authService.getCurrentUser();
    const business = this.authService.getCurrentBusiness();
    const roleName = this.roleId
      ? (this.roles.find((r) => r.id === this.roleId)?.name ?? undefined)
      : undefined;
    return {
      subject: tpl.subject,
      bodyHtml: tpl.bodyHtml,
      bodyText: tpl.bodyText || undefined,
      stylePreset: tpl.stylePreset || undefined,
      primaryColor: tpl.primaryColor || undefined,
      logoUrl: tpl.logoUrl || undefined,
      sampleBusinessName: business?.name ?? undefined,
      sampleRoleName: roleName,
      sampleInvitedBy: user?.fullname ?? undefined,
      sampleMailInvited: this.mail.trim() || undefined,
      sampleExpiresAt: this.expiresAt
        ? this.datetimeService.formatDate(this.expiresAt)
        : undefined,
    };
  }

  // ── Expiración ────────────────────────────────────────────────────────────────

  /**
   * Mínimo del selector de fecha: hoy a las 00:00 (permite expirar HOY mismo).
   * El backend valida el instante exacto elegido y rechaza (400) cualquier fecha/hora
   * que ya haya pasado al momento de enviar el formulario — no la reprograma en silencio.
   */
  get minExpiresAt(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T00:00`;
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  guardar(): void {
    if (!this.mail.trim() || !this.roleId) {
      this.error = 'El correo y el rol son obligatorios.';
      return;
    }

    // No se puede invitar a la misma cuenta con la que se está autenticado.
    // (El backend también lo valida; esto es un atajo de UX.)
    const currentEmail = this.authService
      .getCurrentUser()
      ?.email?.trim()
      .toLowerCase();
    if (currentEmail && currentEmail === this.mail.trim().toLowerCase()) {
      this.error =
        'No puedes invitar a la misma cuenta con la que estás autenticado.';
      this.toast.error({ title: 'Error', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    const request: CreateInvitationRequest = {
      mailInvited: this.mail.trim(),
      roleId: this.roleId,
    };

    if (this.contactId) request.contactId = this.contactId;
    if (this.expiresAt)
      request.expiresAt = this.datetimeService.localToUtcIso(this.expiresAt);
    if (this.templateId) request.templateId = this.templateId;

    this.invitationApi.createInvitation(request).subscribe({
      next: (result) => {
        this.saving = false;
        this.toast.created('Invitación');
        this.saved.emit(result);
      },
      error: (err) => {
        if (
          err?.status === 400 &&
          (err?.error?.message?.toLowerCase().includes('expir') ||
            err?.error?.message?.toLowerCase().includes('valid'))
        ) {
          this.error =
            'La fecha de expiración no es válida según las reglas del sistema. Elige una fecha y hora futura.';
        } else {
          this.error = err.error?.message || 'Error al enviar la invitación';
        }
        this.saving = false;
        this.toast.error({ title: 'Error', message: this.error });
      },
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  reset(): void {
    this.mail = '';
    this.roleId = null;
    this.contactId = null;
    this.expiresAt = '';
    this.expiresTime = '';
    this.contactSearchTerm = '';
    this.contactSearchResults = [];
    this.showContactDropdown = false;
    this.mailAutoFilled = false;
    this.error = '';
    this.saving = false;
    // Re-seleccionar la plantilla default
    const defaultTpl = this.templates.find((t) => t.isDefault);
    this.templateId = defaultTpl?.id ?? null;
    this.selectedTemplate = defaultTpl ?? null;
    // Refrescar vista previa
    this.previewSafeHtml = null;
    this.previewSubject = '';
    this.previewError = '';
    this.preview$.next();
  }
}
